import type { DashboardBarangayStatistics } from "@rbim/shared";
import { Database, Home, MapPinned, UsersRound } from "lucide-react";
import { useEffect, useMemo, useState, type CSSProperties } from "react";

type ScopeType = "PROVINCE" | "MUNICIPALITY_CITY" | "BARANGAY";
type Position = [number, number];
type Geometry = { type: "Polygon"; coordinates: Position[][] } | { type: "MultiPolygon"; coordinates: Position[][][] };
type Feature = { type: "Feature"; geometry: Geometry; properties: Record<string, string | undefined> };
type FeatureCollection = { type: "FeatureCollection"; features: Feature[] };
type BoundaryCandidate = { url: string; relaxed: boolean };

type Props = {
  locationLabel: string;
  scopeType?: ScopeType;
  population?: number | null;
  households?: number | null;
  loading?: boolean;
  preview?: boolean;
  compact?: boolean;
  populationByPsgc?: Record<string, number>;
  barangays?: DashboardBarangayStatistics[];
  onBarangaySelect?: (barangay: DashboardBarangayStatistics | null) => void;
};

const barangayService = "https://ulap-nga.georisk.gov.ph/arcgis/rest/services/PSA/Barangay_2020/MapServer/0/query";
const municipalityService = "https://ulap-nga.georisk.gov.ph/arcgis/rest/services/PSA/Municipal/MapServer/0/query";
const width = 720;
const height = 500;
const padding = 28;
const emptyPopulationByPsgc: Record<string, number> = {};
const emptyBarangays: DashboardBarangayStatistics[] = [];

export function PhilippinesPopulationMap(props: Props) {
  const previewLocation = "Manolo Fortich, Bukidnon";
  const locationLabel = props.preview ? previewLocation : props.locationLabel;
  const scopeType = props.preview ? "MUNICIPALITY_CITY" : props.scopeType ?? inferScopeType(locationLabel);
  const populationByPsgc = props.populationByPsgc ?? emptyPopulationByPsgc;
  const barangays = props.barangays ?? emptyBarangays;
  const names = useMemo(() => parseNames(locationLabel, scopeType), [locationLabel, scopeType]);
  const barangayIndexes = useMemo(() => indexBarangays(barangays), [barangays]);
  const maximumBarangayPopulation = barangays.reduce(
    (maximum, barangay) => Math.max(maximum, barangay.population),
    0,
  );
  const [features, setFeatures] = useState<Feature[]>([]);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapError, setMapError] = useState("");
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setMapLoading(true);
    setMapError("");
    setFeatures([]);
    setSelectedCode(null);

    void loadBoundaryFeatures(scopeType, locationLabel, controller.signal)
      .then((loadedFeatures) => {
        if (!controller.signal.aborted) setFeatures(loadedFeatures);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setMapError(error instanceof Error ? error.message : "Map boundaries could not be loaded.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setMapLoading(false);
      });

    return () => controller.abort();
  }, [scopeType, locationLabel]);

  const project = useMemo(() => projectionFor(features), [features]);
  const selected = features.find((feature) => codeOf(feature) === selectedCode)
    ?? (props.compact ? null : features[0] ?? null);
  const selectedBarangay = selected ? statisticsOf(selected, barangayIndexes) : undefined;
  const selectedPopulation = selected ? populationByPsgc[codeOf(selected)] : undefined;
  const totalPopulation = props.loading ? "…" : typeof props.population === "number" ? props.population.toLocaleString() : "—";
  const totalHouseholds = props.loading ? "…" : typeof props.households === "number" ? props.households.toLocaleString() : "—";
  const child = scopeType === "PROVINCE" ? "Municipality/City" : "Barangay";
  const selectFeature = (feature: Feature) => {
    const code = codeOf(feature);
    setSelectedCode(code);
    props.onBarangaySelect?.(statisticsOf(feature, barangayIndexes) ?? null);
  };

  if (props.compact) {
    return (
      <section style={s.compactPanel} aria-label="Municipality or city map">
        <div style={s.compactHeader}>
          <h2 style={s.compactTitle}>Municipality/City Map</h2>
          <span style={s.mapScope}>{locationLabel}</span>
        </div>
        <div style={s.compactStage}>
          {mapLoading && <div style={s.message}>Loading Barangay map boundaries…</div>}
          {mapError && <div style={s.message}><strong>Map boundary data is not yet available.</strong><span>{mapError}</span></div>}
          {!mapLoading && !mapError && project && (
            <svg viewBox={`0 0 ${width} ${height}`} style={s.svg} role="img" aria-label={mapTitle(scopeType, names)}>
              {features.map((feature) => {
                const code = codeOf(feature);
                const active = selected ? codeOf(selected) === code : false;
                const [x, y] = centerOf(feature, project);
                const statistic = statisticsOf(feature, barangayIndexes);
                const labelFeature = features.length <= 30 || Boolean(statistic?.population);
                return (
                  <g
                    key={code}
                    className="map-barangay"
                    role="button"
                    tabIndex={0}
                    aria-label={barangayAriaLabel(feature, statistic)}
                    onClick={() => selectFeature(feature)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        selectFeature(feature);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <path
                      d={pathOf(feature.geometry, project)}
                      fill={populationFill(statistic?.population ?? 0, maximumBarangayPopulation, active)}
                      stroke={active ? "#0f684c" : "#8bb9a8"}
                      strokeWidth={active ? 2.8 : 1.1}
                    >
                      <title>{barangayAriaLabel(feature, statistic)}</title>
                    </path>
                    {labelFeature ? (
                      <text x={x} y={y - 5} textAnchor="middle" dominantBaseline="middle" fontSize={features.length > 30 ? 7.5 : 9.5} fontWeight={750} fill="#244a40" pointerEvents="none">
                        <tspan x={x}>{short(nameOf(feature))}</tspan>
                        {statistic ? <tspan x={x} dy="11">{statistic.population.toLocaleString()}</tspan> : null}
                      </text>
                    ) : null}
                  </g>
                );
              })}
            </svg>
          )}
        </div>
        <BarangayStatisticsDetails statistic={selectedBarangay ?? null} />
        <div style={s.populationLegend}>
          <strong>Population Density</strong>
          <i style={s.populationGradient} />
          <span>Low</span>
          <span style={{ textAlign: "right" }}>High</span>
        </div>
        {!props.loading && barangays.every((barangay) => barangay.population === 0) ? (
          <p style={s.noMapRecords}>No Barangay population records available.</p>
        ) : null}
      </section>
    );
  }

  return (
    <section style={s.panel} aria-label="RBIM geographic population map">
      <div style={s.mapArea}>
        <div style={s.heading}>
          <div>
            <span style={s.eyebrow}>Authorized RBIM coverage</span>
            <h2 style={s.title}>{mapTitle(scopeType, names)}</h2>
            <p style={s.subtitle}>{mapSubtitle(scopeType, names)}</p>
          </div>
          <span style={s.pill}><MapPinned size={15} />{scopeLabel(scopeType)}</span>
        </div>

        <div style={s.stage}>
          {mapLoading && <div style={s.message}>Loading administrative boundaries…</div>}
          {mapError && <div style={s.message}><strong>Map unavailable</strong><span>{mapError}</span></div>}
          {!mapLoading && !mapError && project && (
            <svg viewBox={`0 0 ${width} ${height}`} style={s.svg} role="img" aria-label={mapTitle(scopeType, names)}>
              {features.map((feature) => {
                const code = codeOf(feature);
                const active = selected ? codeOf(selected) === code : false;
                const [x, y] = centerOf(feature, project);
                return (
                  <g key={code}>
                    <path
                      d={pathOf(feature.geometry, project)}
                      fill={active ? "#dceee8" : "#eef5f3"}
                      stroke={active ? "#176453" : "#6f9189"}
                      strokeWidth={active ? 2.5 : 1.25}
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedCode(code)}
                    >
                      <title>{nameOf(feature)} — RBIM population {populationText(populationByPsgc[code])}</title>
                    </path>
                    {features.length <= 30 && <text x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize={features.length > 20 ? 8.5 : 10} fontWeight={700} fill="#274d48" pointerEvents="none">{short(nameOf(feature))}</text>}
                  </g>
                );
              })}
            </svg>
          )}
          <div style={s.totalBubble}><span>Total RBIM Population</span><strong>{totalPopulation}</strong><small>{typeof props.population === "number" ? "people in authorized scope" : "awaiting synchronized records"}</small></div>
        </div>

        <div style={s.legend}><span><i style={s.activeKey} />Selected {child}</span><span><i style={s.defaultKey} />Other {child === "Barangay" ? "barangays" : "municipalities/cities"}</span><span>{features.length ? `${features.length} areas shown` : ""}</span></div>
        <small style={s.source}>Administrative boundaries are reference geography; RBIM population counts come only from authorized synchronized RBIM records.</small>
      </div>

      <aside style={s.summary}>
        <span style={s.eyebrow}>Population at a glance</span>
        <h3 style={s.summaryTitle}>{locationLabel}</h3>
        <p style={s.summaryText}>The dashboard map automatically changes detail according to the signed-in user&apos;s approved location.</p>
        <div style={s.statGrid}>
          <Stat icon={UsersRound} label="Total Population" value={totalPopulation} />
          <Stat icon={Home} label="Total Households" value={totalHouseholds} />
        </div>
        <div style={s.selectedBox}>
          <span style={s.eyebrow}>Selected {child}</span>
          <strong style={s.selectedName}>{selected ? nameOf(selected) : "—"}</strong>
          <div style={s.selectedRow}><span>RBIM Population</span><strong>{populationText(selectedPopulation)}</strong></div>
          <small style={s.hint}>When per-{child.toLowerCase()} synchronized counts are available, clicking an area will show its exact RBIM population.</small>
        </div>
        <div style={s.status}><Database size={19} /><div><strong>{typeof props.population === "number" ? "Synchronized RBIM data" : "Population data not yet synchronized"}</strong><span>{props.preview ? "Preview uses real map boundaries but intentionally leaves RBIM counts blank." : "Population indicators update from authorized synchronized records."}</span></div></div>
      </aside>
    </section>
  );
}

function Stat({ icon: Icon, label, value }: { icon: typeof UsersRound; label: string; value: string }) {
  return <div style={s.stat}><span style={s.statIcon}><Icon size={20} /></span><span>{label}</span><strong>{value}</strong></div>;
}

function inferScopeType(label: string): ScopeType {
  const count = label.split(",").map((part) => part.trim()).filter(Boolean).length;
  return count >= 3 ? "BARANGAY" : count === 2 ? "MUNICIPALITY_CITY" : "PROVINCE";
}

function parseNames(label: string, scope: ScopeType) {
  const parts = label.split(",").map((part) => part.trim()).filter(Boolean);
  if (scope === "BARANGAY") return { barangay: parts[0] ?? "", municipality: parts[1] ?? "", province: parts[2] ?? "" };
  if (scope === "MUNICIPALITY_CITY") return { barangay: "", municipality: parts[0] ?? "", province: parts[1] ?? "" };
  return { barangay: "", municipality: "", province: parts[0] ?? "" };
}

export function buildBoundaryCandidates(scope: ScopeType, locationLabel: string): BoundaryCandidate[] {
  const names = parseNames(locationLabel, scope);
  const queryBase = { f: "geojson", returnGeometry: "true", outSR: "4326", geometryPrecision: "6" };

  if (scope === "PROVINCE") {
    const params = new URLSearchParams(queryBase);
    params.set("where", `prov_name='${escaped(names.province)}'`);
    params.set("outFields", "reg_name,prov_name,city_name,city_code,psgc_10d");
    return [{ url: `${municipalityService}?${params}`, relaxed: false }];
  }

  const coreWhere = [`UPPER(city_name)=UPPER('${escaped(names.municipality)}')`];
  if (scope === "BARANGAY") coreWhere.push(`UPPER(brgy_name)=UPPER('${escaped(names.barangay)}')`);

  const whereCandidates: Array<{ where: string[]; relaxed: boolean }> = [];
  if (names.province) {
    whereCandidates.push({ where: [`UPPER(prov_name)=UPPER('${escaped(names.province)}')`, ...coreWhere], relaxed: false });
    whereCandidates.push({ where: [`UPPER(reg_name)=UPPER('${escaped(names.province)}')`, ...coreWhere], relaxed: false });
  }
  whereCandidates.push({ where: coreWhere, relaxed: Boolean(names.province) });

  return whereCandidates.map(({ where, relaxed }) => {
    const params = new URLSearchParams(queryBase);
    params.set("where", where.join(" AND "));
    params.set("outFields", "reg_name,prov_name,city_name,brgy_name,brgy_code,city_code,psgc_10d,old_names,bgyarea_ha");
    return { url: `${barangayService}?${params}`, relaxed };
  });
}

export async function loadBoundaryFeatures(
  scope: ScopeType,
  locationLabel: string,
  signal?: AbortSignal,
  request: typeof fetch = fetch,
): Promise<Feature[]> {
  const primaryCandidates = buildBoundaryCandidates(scope, locationLabel);
  const legacyCandidates = scope === "PROVINCE"
    ? []
    : primaryCandidates.map((candidate) => ({
        ...candidate,
        url: candidate.url.replace("/PSA/Barangay_2020/", "/PSA/BarangayPopMF/"),
      }));
  const candidates = [...primaryCandidates, ...legacyCandidates];

  for (const candidate of candidates) {
    const response = await request(candidate.url, { signal });
    if (!response.ok) throw new Error(`Boundary service returned ${response.status}.`);

    const data = await response.json() as FeatureCollection;
    if (!data.features?.length) continue;

    if (candidate.relaxed && hasAmbiguousParents(data.features)) {
      throw new Error("The assigned Municipality/City name matches more than one geographic area. Update the assigned location with an exact Province or Region.");
    }

    return data.features;
  }

  throw new Error("No matching boundaries were returned for this assigned location.");
}

function hasAmbiguousParents(features: Feature[]) {
  const parents = new Set(
    features.map((feature) => [
      feature.properties.reg_name ?? "",
      feature.properties.prov_name ?? "",
      feature.properties.city_name ?? "",
    ].join("|")),
  );
  return parents.size > 1;
}

function escaped(value: string) { return value.replaceAll("'", "''"); }
function mapTitle(scope: ScopeType, n: ReturnType<typeof parseNames>) { return scope === "PROVINCE" ? `Municipalities and Cities of ${n.province}` : scope === "MUNICIPALITY_CITY" ? `Barangays of ${n.municipality}` : n.barangay; }
function mapSubtitle(scope: ScopeType, n: ReturnType<typeof parseNames>) { return scope === "PROVINCE" ? `Select a municipality or city within ${n.province}.` : scope === "MUNICIPALITY_CITY" ? `${n.municipality}, ${n.province} — select a barangay to view its RBIM population.` : `${n.barangay}, ${n.municipality}, ${n.province}`; }
function scopeLabel(scope: ScopeType) { return scope === "PROVINCE" ? "Province scope" : scope === "MUNICIPALITY_CITY" ? "Municipality/City scope" : "Barangay scope"; }
function nameOf(f: Feature) { return f.properties.brgy_name ?? f.properties.city_name ?? f.properties.prov_name ?? "Unnamed area"; }
function codeOf(f: Feature) { return f.properties.psgc_10d ?? f.properties.brgy_code ?? f.properties.city_code ?? nameOf(f); }
function populationText(value: number | undefined) { return typeof value === "number" ? `${value.toLocaleString()} people` : "—"; }
function short(value: string) { return value.length <= 17 ? value : `${value.slice(0, 15)}…`; }
function normalizeGeographyName(value: string) { return value.toLowerCase().replace(/\b(barangay|brgy\.?)[\s-]*/g, "").replace(/[^a-z0-9]/g, ""); }
function normalizePsgc(value: string) { return value.replace(/\D/g, "").replace(/^0+/, ""); }
function indexBarangays(barangays: DashboardBarangayStatistics[]) {
  return {
    byCode: new Map(barangays.map((barangay) => [normalizePsgc(barangay.psgcCode), barangay])),
    byName: new Map(barangays.map((barangay) => [normalizeGeographyName(barangay.barangayName), barangay])),
  };
}
function statisticsOf(feature: Feature, indexes: ReturnType<typeof indexBarangays>) {
  const code = normalizePsgc(codeOf(feature));
  return indexes.byCode.get(code) ?? indexes.byName.get(normalizeGeographyName(nameOf(feature)));
}
function populationFill(population: number, maximum: number, active: boolean) {
  if (active) return "#4f9e76";
  if (maximum <= 0 || population <= 0) return "#edf5f0";
  const ratio = Math.min(1, population / maximum);
  return `hsl(145 34% ${91 - ratio * 39}%)`;
}
function barangayAriaLabel(feature: Feature, statistic: DashboardBarangayStatistics | undefined) {
  if (!statistic) return `${nameOf(feature)}. No synchronized RBIM population available.`;
  return `${statistic.barangayName}. Population ${statistic.population.toLocaleString()}, households ${statistic.households.toLocaleString()}, families ${statistic.families.toLocaleString()}, non-migrants ${statistic.nonMigrants.toLocaleString()}, migrants ${statistic.migrants.toLocaleString()}, transient ${statistic.transient.toLocaleString()}.`;
}

export function BarangayStatisticsDetails({ statistic }: { statistic: DashboardBarangayStatistics | null }) {
  if (!statistic) {
    return (
      <section style={s.selectedDetails} aria-live="polite" aria-label="Selected Barangay migration profile">
        <span style={s.eyebrow}>Barangay Details / Migration Profile</span>
        <p style={s.selectedEmpty}>Select a barangay on the map to view its population and migration profile.</p>
      </section>
    );
  }

  return (
    <section style={s.selectedDetails} aria-live="polite" aria-label="Selected Barangay migration profile">
      <span style={s.eyebrow}>Selected Barangay</span>
      <h3 style={s.selectedBarangayName}>{statistic.barangayName}</h3>
      <div style={s.selectedPrimaryStats}>
        <div style={s.selectedPrimaryStat}><span>Population</span><strong style={s.selectedMetricValue}>{statistic.population.toLocaleString()}</strong><small>Residents</small></div>
        <div style={s.selectedPrimaryStat}><span>Households</span><strong style={s.selectedMetricValue}>{statistic.households.toLocaleString()}</strong><small>Households</small></div>
      </div>
      <div style={s.migrationProfile}>
        <strong style={s.migrationTitle}>Migration Status</strong>
        <div style={s.migrationRow}><span>Non-Migrant</span><b>{statistic.nonMigrants.toLocaleString()}</b></div>
        <div style={s.migrationRow}><span>Migrant</span><b>{statistic.migrants.toLocaleString()}</b></div>
        <div style={s.migrationRow}><span>Transient</span><b>{statistic.transient.toLocaleString()}</b></div>
      </div>
    </section>
  );
}
function pointsOf(g: Geometry): Position[] { return g.type === "Polygon" ? g.coordinates.flat() : g.coordinates.flat(2); }
function projectionFor(features: Feature[]) {
  const pts = features.flatMap((f) => pointsOf(f.geometry));
  if (!pts.length) return null;
  const xs = pts.map(([x]) => x), ys = pts.map(([, y]) => y);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const sx = Math.max(maxX - minX, .0001), sy = Math.max(maxY - minY, .0001);
  const scale = Math.min((width - padding * 2) / sx, (height - padding * 2) / sy);
  const ox = (width - sx * scale) / 2, oy = (height - sy * scale) / 2;
  return ([x, y]: Position): Position => [ox + (x - minX) * scale, oy + (maxY - y) * scale];
}
function pathOf(g: Geometry, project: (p: Position) => Position) {
  const polygons = g.type === "Polygon" ? [g.coordinates] : g.coordinates;
  return polygons.map((polygon) => polygon.map((ring) => ring.map((p, i) => { const [x, y] = project(p); return `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`; }).join(" ") + " Z").join(" ")).join(" ");
}
function centerOf(f: Feature, project: (p: Position) => Position): Position {
  const pts = pointsOf(f.geometry), xs = pts.map(([x]) => x), ys = pts.map(([, y]) => y);
  return project([(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2]);
}

const s: Record<string, CSSProperties> = {
  compactPanel: { minWidth: 0, overflow: "hidden", padding: 18, border: "1px solid #dfe6ee", borderRadius: 12, background: "#fff", boxShadow: "0 4px 16px #1834510a" },
  compactHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 13 },
  compactTitle: { margin: 0, color: "#18395f", fontSize: 16 },
  mapScope: { overflow: "hidden", color: "#76859a", fontSize: 11, textOverflow: "ellipsis", whiteSpace: "nowrap" },
  compactStage: { position: "relative", minHeight: 370, display: "grid", placeItems: "center", overflow: "hidden", border: "1px solid #e2ebe7", borderRadius: 9, background: "#f8fbfa" },
  selectedDetails: { display: "grid", gap: 12, marginTop: 14, padding: 16, border: "1px solid #d8e5df", borderRadius: 10, background: "#fbfdfc", color: "#52647b" },
  selectedEmpty: { margin: 0, color: "#6d7c8e", fontSize: 12, lineHeight: 1.55 },
  selectedBarangayName: { margin: "-5px 0 0", color: "#173968", fontSize: 19 },
  selectedPrimaryStats: { display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 10 },
  selectedPrimaryStat: { display: "grid", gap: 2, padding: 12, border: "1px solid #e0e8ee", borderRadius: 8, background: "#fff", fontSize: 11 },
  selectedMetricValue: { color: "#173968", fontSize: 20 },
  migrationProfile: { display: "grid", gap: 0, borderTop: "1px solid #e1e9e5", paddingTop: 10 },
  migrationTitle: { marginBottom: 4, color: "#244a40", fontSize: 12 },
  migrationRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "7px 0", borderBottom: "1px solid #edf1f4", fontSize: 12 },
  populationLegend: { width: 190, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "5px 8px", margin: "12px 0 0 auto", color: "#6e7c8d", fontSize: 9 },
  populationGradient: { gridColumn: "1 / -1", height: 10, borderRadius: 3, background: "linear-gradient(90deg,#edf5f0,#78b78e,#1d684b)" },
  noMapRecords: { margin: "10px 0 0", color: "#7a8798", fontSize: 11, textAlign: "center" },
  panel: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", overflow: "hidden", border: "1px solid #dbe3ed", borderRadius: 14, background: "#fff", boxShadow: "0 8px 24px #213b5b0d" },
  mapArea: { minWidth: 0, padding: 22, background: "linear-gradient(145deg,#eef5f4,#f7fafc 62%,#eef3f8)", borderRight: "1px solid #e2e8f0" },
  heading: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 14 },
  eyebrow: { display: "block", color: "#55708e", textTransform: "uppercase", letterSpacing: ".09em", fontSize: 11, fontWeight: 800 },
  title: { margin: "5px 0 3px", color: "#193b67", fontSize: 20 }, subtitle: { margin: 0, color: "#6b7a8d", fontSize: 12 },
  pill: { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 999, background: "#e5f5ee", color: "#21644f", fontSize: 12, fontWeight: 800 },
  stage: { position: "relative", minHeight: 430, display: "grid", placeItems: "center", overflow: "hidden", border: "1px solid #d9e5e3", borderRadius: 12, background: "#e8f1ef" },
  svg: { width: "100%", maxHeight: 520 }, message: { display: "grid", gap: 7, textAlign: "center", padding: 30, color: "#65758a", fontSize: 13 },
  totalBubble: { position: "absolute", top: 16, right: 16, minWidth: 175, padding: "13px 15px", borderRadius: 11, background: "#163b69ed", color: "#fff", boxShadow: "0 10px 26px #17396826", pointerEvents: "none" },
  legend: { display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10, color: "#65758a", fontSize: 11 }, activeKey: { display: "inline-block", width: 10, height: 10, marginRight: 5, background: "#dceee8", border: "2px solid #176453" }, defaultKey: { display: "inline-block", width: 10, height: 10, marginRight: 5, background: "#eef5f3", border: "1px solid #6f9189" }, source: { display: "block", marginTop: 8, color: "#7c8999", fontSize: 10 },
  summary: { padding: 26, display: "flex", flexDirection: "column", justifyContent: "center" }, summaryTitle: { margin: "7px 0", color: "#173968", fontSize: 23 }, summaryText: { margin: 0, color: "#68768a", fontSize: 13, lineHeight: 1.6 },
  statGrid: { display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 12, margin: "22px 0" }, stat: { padding: 15, border: "1px solid #dde6ee", borderRadius: 10, background: "#f8fafc" }, statIcon: { width: 36, height: 36, display: "grid", placeItems: "center", borderRadius: 9, background: "#e9f4f0", color: "#1b624f", marginBottom: 11 },
  selectedBox: { marginBottom: 18, padding: 16, border: "1px solid #dce6ee", borderRadius: 10 }, selectedName: { display: "block", margin: "6px 0 11px", color: "#173968", fontSize: 18 }, selectedRow: { display: "flex", justifyContent: "space-between", gap: 12, paddingTop: 10, borderTop: "1px solid #e7edf3", color: "#617089", fontSize: 12 }, hint: { display: "block", marginTop: 9, color: "#8793a3", fontSize: 10 },
  status: { display: "flex", gap: 11, padding: 14, borderRadius: 10, background: "#f2f6fa", color: "#52647b", fontSize: 11 },
};
